/**
 * CartBaseStoreAPIInterceptor - Validates baseStore before API calls
 * Prevents stale baseStore requests and refreshes data when baseStore changes
 */

import cartBaseStoreManager from './CartBaseStoreManager';

class CartBaseStoreAPIInterceptor {
  /**
   * Create request interceptor for GraphQL/REST APIs
   * Validates cartId and baseStore before sending requests
   * 
   * Usage with Apollo Client:
   * client.link = new ApolloLink((operation, forward) => {
   *   const validationResult = cartInterceptor.validateRequest(operation);
   *   if (!validationResult.isValid) {
   *     return new Observable(observer => observer.error(validationResult.error));
   *   }
   *   return forward(operation);
   * });
   */
  // eslint-disable-next-line class-methods-use-this
  validateRequest(operation) {
    try {
      // Get cartId and baseStore from operation context or variables
      const context = operation.getContext?.() || {};
      const variables = operation.variables || {};
      
      const cartId = variables.cartId || context.cartId;
      const baseStore = variables.baseStore || context.baseStore;
      
      // If no cartId/baseStore, request is allowed
      if (!cartId || !baseStore) {
        return { isValid: true };
      }

      // CRITICAL: Check if request baseStore matches the registered cart's baseStore
      const registeredStore = cartBaseStoreManager.getCartBaseStore(cartId);

      // If cart is not registered yet, allow request (first-time cart fetch)
      if (!registeredStore) {
        // eslint-disable-next-line no-console
        console.log(`ℹ️  Cart ${cartId} not yet registered, allowing initial request with baseStore ${baseStore}`);
        return { isValid: true };
      }

      // STRICT CHECK: Verify baseStore matches registered cart exactly
      if (!cartBaseStoreManager.verifyBaseStore(cartId, baseStore)) {
        const error = new Error(
          `BaseStore mismatch for cart ${cartId}. Expected ${registeredStore}, got ${baseStore}`
        );
        error.code = 'BASESTORE_MISMATCH';
        error.cartId = cartId;
        error.expectedBaseStore = registeredStore;
        error.receivedBaseStore = baseStore;
        
        // eslint-disable-next-line no-console
        console.error(`❌ BLOCKED: ${error.message}`);
        
        return {
          isValid: false,
          error,
          shouldRefresh: true
        };
      }

      return { isValid: true };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error validating request:', error);
      return { isValid: true }; // Allow request on validation error
    }
  }

  /**
   * Handle API error related to baseStore/cart mismatch
   * @param {Error} error - API error
   * @param {string} cartId - Cart identifier
   * @returns {Object} - { shouldRefresh, newBaseStore }
   */
  // eslint-disable-next-line class-methods-use-this
  handleAPIError(error, cartId) {
    // Check if error is related to cart not found
    if (error?.message?.includes('cart not found') || error?.message?.includes('Cart not found')) {
      // eslint-disable-next-line no-console
      console.error(`❌ Cart ${cartId} not found - baseStore may have changed`);
      
      // Get current registered baseStore
      const currentStore = cartBaseStoreManager.getCartBaseStore(cartId);
      
      // Invalidate cache and trigger refresh
      cartBaseStoreManager.invalidateCartCache(cartId);
      
      return {
        shouldRefresh: true,
        newBaseStore: currentStore,
        error: new Error(`Cart ${cartId} not found. BaseStore: ${currentStore}`)
      };
    }

    // Check for baseStore mismatch in error message
    if (error?.code === 'BASESTORE_MISMATCH') {
      const newStore = error.expectedBaseStore;
      // eslint-disable-next-line no-console
      console.error(`❌ BaseStore mismatch for cart ${cartId}. Switching to ${newStore}`);
      
      cartBaseStoreManager.invalidateCartCache(cartId);
      
      return {
        shouldRefresh: true,
        newBaseStore: newStore,
        error
      };
    }

    return { shouldRefresh: false };
  }

  /**
   * Create wrapped fetch function
   * @param {Function} originalFetch - Original fetch function
   * @returns {Function} - Wrapped fetch function
   */
  wrapFetch(originalFetch) {
    return async (url, options = {}) => {
      try {
        const response = await originalFetch(url, options);
        
        // Handle error responses
        if (!response.ok && response.status === 404) {
          const data = await response.json().catch(() => ({}));
          if (data.message?.includes('cart') || data.message?.includes('Cart')) {
            // Extract cartId from request if possible
            const cartId = this.extractCartIdFromRequest(url, options);
            if (cartId) {
              const error = new Error(data.message || 'Cart not found');
              error.code = 'CART_NOT_FOUND';
              error.cartId = cartId;
              const result = this.handleAPIError(error, cartId);
              if (result.shouldRefresh) {
                // Emit event to trigger refresh
                window.dispatchEvent(new CustomEvent('cartRefreshNeeded', {
                  detail: { cartId, newBaseStore: result.newBaseStore }
                }));
              }
            }
          }
        }
        
        return response;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Fetch error:', error);
        throw error;
      }
    };
  }

  /**
   * Extract cartId from request URL or body
   * @private
   */
  // eslint-disable-next-line class-methods-use-this
  extractCartIdFromRequest(url, options) {
    // Try to extract from URL
    const cartIdMatch = url.match(/cart[Id]*[=/]([a-zA-Z0-9-]+)/i);
    if (cartIdMatch) {
      return cartIdMatch[1];
    }

    // Try to extract from request body
    try {
      if (options.body) {
        const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
        return body.cartId || body.variables?.cartId;
      }
    } catch (e) {
      // Ignore parse errors
    }

    return null;
  }

  /**
   * Create middleware for Redux or other state management
   */
  createMiddleware() {
    return () => (next) => (action) => {
      // Intercept cart/baseStore related actions
      if (action.type?.includes('CART') || action.type?.includes('BASESTORE')) {
        const { cartId, baseStore } = action.payload || {};
        
        if (cartId && baseStore) {
          const validation = this.validateRequest({
            variables: { cartId, baseStore },
            getContext: () => ({})
          });
          
          if (!validation.isValid) {
            // eslint-disable-next-line no-console
            console.error('Request blocked:', validation.error.message);
            return undefined; // Block the action
          }
        }
      }
      
      return next(action);
    };
  }
}

export default new CartBaseStoreAPIInterceptor();
